import { useState } from 'react';
import { useRecentCommits, useCommitStats } from '@/hooks/useCommits';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  GitCommit,
  Search,
  Clock,
  TrendingUp,
  ExternalLink,
  FileCode,
  Users,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';
import { Commit } from '@/types';

/**
 * Commit card component
 */
function CommitCard({ commit }: { commit: Commit }) {
  const repoName = typeof commit.repoId === 'object' ? commit.repoId.name : 'Unknown';

  return (
    <Card className="hover:border-primary/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <GitCommit className="h-5 w-5 mt-1 text-primary flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-medium">{commit.title}</p>
                {commit.message && commit.message !== commit.title && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {commit.message}
                  </p>
                )}
              </div>
              {commit.webUrl && (
                <a
                  href={commit.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary flex-shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {commit.authorName}
              </span>
              <span className="flex items-center gap-1">
                <FileCode className="h-3 w-3" />
                {repoName}
              </span>
              {commit.branch && (
                <span className="bg-muted px-1.5 py-0.5 rounded text-xs">
                  {commit.branch}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(commit.committedAt)}
              </span>
            </div>

            <div className="flex items-center gap-4 mt-2">
              <span className="text-xs">
                <span className="text-green-600 font-medium">+{commit.additions}</span>
                {' / '}
                <span className="text-red-600 font-medium">-{commit.deletions}</span>
              </span>
              <span className="text-xs text-muted-foreground">
                {commit.filesChanged} file{commit.filesChanged !== 1 ? 's' : ''} changed
              </span>
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                {commit.shortSha}
              </code>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Activity() {
  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState(20);

  // Fetch data
  const { data: commits, isLoading } = useRecentCommits(limit);
  const { data: stats } = useCommitStats();

  // Filter commits by search
  const filteredCommits = commits?.filter((commit) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      commit.title.toLowerCase().includes(query) ||
      commit.authorName.toLowerCase().includes(query) ||
      commit.message?.toLowerCase().includes(query) ||
      (typeof commit.repoId === 'object' && commit.repoId.name.toLowerCase().includes(query))
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Activity</h1>
        <p className="text-muted-foreground">
          Recent commits and changes across all repositories
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Commits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCommits || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Lines Added</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{stats?.totalAdditions?.toLocaleString() || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Lines Removed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              -{stats?.totalDeletions?.toLocaleString() || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Contributors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.uniqueAuthors || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Commit Activity Chart */}
      {stats?.commitsByDay && stats.commitsByDay.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Commit Activity (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[120px] flex items-end gap-1">
              {stats.commitsByDay.slice(-30).map((day) => (
                <div
                  key={day.date}
                  className="flex-1 bg-primary/20 hover:bg-primary/40 transition-colors rounded-t cursor-pointer"
                  style={{
                    height: `${Math.max(
                      5,
                      (day.count / Math.max(...stats.commitsByDay.map((d) => d.count))) * 100
                    )}%`,
                  }}
                  title={`${day.date}: ${day.count} commits`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>30 days ago</span>
              <span>Today</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search commits by message, author, or repository..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Commit List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !filteredCommits?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitCommit className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No commits found</h3>
            <p className="text-muted-foreground">
              {searchQuery
                ? 'Try a different search term'
                : 'Sync repositories from GitLab to see activity'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {filteredCommits.map((commit) => (
              <CommitCard key={commit.id} commit={commit} />
            ))}
          </div>

          {/* Load More */}
          {commits && commits.length === limit && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setLimit((l) => l + 20)}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
